import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import { useCustomerViewQuery } from "@/v2-backoffice-client/customer/view/customer.view.query"
import { useTenantUserQuery } from "@/v2-backoffice-client/tenant/user/tenant.user.query"
import TenantCustomerAddSelectorItem from "@/v2-backoffice-client/tenant/customer/add/TenantCustomerAddSelectorItem"

export type TenantCustomerAddSelectorDialogProps = {
  open: boolean
  onOpenChange(open: boolean): void
  tenantId: number
  tenantName?: string
  externalCustomerId?: string | null
}

function TenantCustomerAddSelectorDialog(
  props: TenantCustomerAddSelectorDialogProps,
) {
  const { open, onOpenChange, tenantId, tenantName, externalCustomerId } = props

  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data: currentData } = useCustomerViewQuery({
    skip: !open || !externalCustomerId,
    variables: { externalId: externalCustomerId ?? "" },
  })

  // The team's live members, for the guard on each candidate.
  const { data: memberData } = useTenantUserQuery({
    skip: !open,
    variables: { tenantId, onlyDeleted: false },
  })
  const members = (memberData?.getUserTenantsByTenantId ?? []).flatMap(
    (userTenant) => (userTenant.User ? [userTenant.User] : []),
  )

  const { data, loading, error } = useCustomerQuery({
    skip: !open || search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for customers",
  })

  const activeCustomerId = selectedId ?? externalCustomerId
  const searchResults = data?.getCustomers.items ?? []
  const currentCustomer = currentData?.getCustomerByExternalId
  const filteredResults = searchResults.filter(
    (c) => c.Id !== currentCustomer?.Id,
  )

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setSelectedId(null)
      setSearch("")
    }
  }

  const title = tenantName
    ? `Update Customer for ${tenantName}`
    : "Update Customer"

  return (
    <Selector.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description="The customer this team belongs to, whose license its members' seats count against. A customer that owns none of the members takes a confirmation."
    >
      <Selector.Search
        placeholder="Search by customer name..."
        onSearch={setSearch}
      />

      <Selector.List
        emptyLabel="No customers found"
        isLoading={loading}
        error={error}
        noResults={filteredResults.length === 0}
        pinned={
          currentCustomer && (
            <TenantCustomerAddSelectorItem
              customer={currentCustomer}
              added={currentCustomer.ExternalCustomerId === activeCustomerId}
              tenantId={tenantId}
              members={members}
              onSelect={() => {
                setSelectedId(currentCustomer.ExternalCustomerId ?? null)
              }}
              onDeselect={() => {
                setSelectedId(null)
              }}
            />
          )
        }
      >
        {filteredResults.map((customer) => (
          <TenantCustomerAddSelectorItem
            key={customer.Id}
            customer={customer}
            added={customer.ExternalCustomerId === activeCustomerId}
            tenantId={tenantId}
            members={members}
            onSelect={() => {
              setSelectedId(customer.ExternalCustomerId ?? null)
            }}
            onDeselect={() => {
              setSelectedId(null)
            }}
          />
        ))}
      </Selector.List>
    </Selector.Dialog>
  )
}

export default TenantCustomerAddSelectorDialog
